import { ChevronLeft } from 'lucide-react';
import React, { ReactElement, ReactNode, useCallback, useEffect } from 'react';
import {
  Controller,
  ControllerFieldState,
  ControllerProps,
  ControllerRenderProps,
  FieldValues,
  Path,
  PathValue,
  useFormContext,
} from 'react-hook-form';

import { CircleQuestionmarkIcon, Tooltip, TooltipContent, TooltipProvider, TooltipTrigger, cn } from '@column/ui';

import { UnderlineLink } from '~/elements/UnderlineLink';
import { HelpSidebarCustomType, useHelpSidebarStore } from '~/stores/HelpSidebar';

export interface FormFieldError {
  title: string;
  subtitle?: string;
  /** Renders the subtitle as a link. */
  onClick?: () => void;
}

export type FormFieldProps<T extends FieldValues> = {
  id: Path<T>;
  label?: string;
  labelVariant?: 'default' | 'primary';
  description?: ReactNode;
  tooltip?: string | HelpSidebarCustomType;
  rules?: ControllerProps<T, Path<T>>['rules'];
  defaultValue?: PathValue<T, Path<T>> | undefined;
  children: (field: ControllerRenderProps<T, Path<T>>, fieldState: ControllerFieldState) => ReactElement | ReactNode;
  updateOn?: Path<T>;
  compact?: boolean;
  hide?: boolean;
  oneColumn?: boolean;
  noStyle?: boolean;
  error?: FormFieldError;
  /** Validate this field live, before the form is submitted. */
  validationMode?: 'onChange' | 'onBlur';
};

const TooltipIcon = ({ content }: { content: string }) => (
  <TooltipProvider>
    <Tooltip>
      <TooltipTrigger asChild>
        <span className="ml-1 inline-flex align-top text-gray-350 transition-colors hover:text-gray-500 dark:text-gray-600 dark:hover:text-gray-400">
          <CircleQuestionmarkIcon />
        </span>
      </TooltipTrigger>
      <TooltipContent className="max-w-xs">{content}</TooltipContent>
    </Tooltip>
  </TooltipProvider>
);

const HelpSidebarIcon = ({ onClick }: { onClick: () => void }) => (
  <TooltipProvider>
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          type="button"
          aria-label="Learn more"
          onClick={onClick}
          className="group/help relative ml-1 inline-flex cursor-pointer align-top"
        >
          <span className="absolute inset-[1.5px] rounded-full bg-foreground/90 opacity-0 transition-opacity duration-100 group-hover/help:opacity-100 group-hover/help:duration-200" />
          <CircleQuestionmarkIcon className="relative text-gray-350 transition-opacity duration-200 group-hover/help:opacity-0 group-hover/help:duration-100 dark:text-gray-600" />
          <ChevronLeft
            strokeWidth={2.5}
            className="absolute inset-0 m-auto size-3 translate-x-1 text-background opacity-0 transition-[translate,opacity] duration-100 group-hover/help:translate-x-0 group-hover/help:opacity-100 group-hover/help:duration-200"
          />
        </button>
      </TooltipTrigger>
      <TooltipContent>Click to learn more</TooltipContent>
    </Tooltip>
  </TooltipProvider>
);

export const FormField = <T extends FieldValues>({
  id,
  label,
  labelVariant,
  rules,
  defaultValue,
  children,
  description,
  tooltip,
  updateOn,
  compact,
  oneColumn,
  noStyle,
  hide,
  error,
  validationMode,
}: FormFieldProps<T>) => {
  const { control, watch, register, trigger } = useFormContext<T>();

  const openHelpSidebarCustom = useHelpSidebarStore((s) => s.openHelpSidebarCustom);

  const watchValue = updateOn ? watch(updateOn) : undefined;

  const handleTooltipClick = useCallback(() => {
    if (typeof tooltip === 'string' || typeof tooltip === 'undefined') {
      return;
    }

    openHelpSidebarCustom(tooltip);
  }, [tooltip]);

  useEffect(() => {
    if (!hide) {
      register(id);
    }
  }, [hide, id]);

  if (hide) {
    return null;
  }

  const sideBySide = !oneColumn;

  return (
    <div className="@container/form-field">
      <div
        className={cn(
          'form-field',
          !noStyle && [
            'grid grid-cols-[auto] grid-rows-[auto] items-start gap-x-6 gap-y-2',
            sideBySide && [
              '@tablet/form-field:grid-rows-[auto_auto] @tablet/form-field:gap-y-0 @wide/form-field:grid-rows-[auto]',
              // minmax(0, 1fr) lets long unbreakable values truncate instead of widening the field.
              !compact &&
                '@tablet/form-field:grid-cols-[minmax(180px,25%)_minmax(0,1fr)] @wide/form-field:grid-cols-[minmax(180px,25%)_auto_25%]',
            ],
            compact && 'grid-cols-[fit-content(100%)_fit-content(100%)]',
          ]
        )}
      >
        {!noStyle && (
          <div className="grid gap-0 leading-none @tablet/form-field:gap-1">
            <div
              className={cn(
                'text-sm leading-[18px] font-medium',
                labelVariant === 'primary' ? 'text-primary' : 'text-foreground',
                sideBySide && '@tablet/form-field:mt-3'
              )}
            >
              {label}
              {!!rules?.required && <span className="ml-0.5 inline-block font-bold text-destructive">*</span>}

              {typeof tooltip === 'string' ? (
                <TooltipIcon content={tooltip} />
              ) : (
                typeof tooltip === 'object' && <HelpSidebarIcon onClick={handleTooltipClick} />
              )}
            </div>
            {description && (
              // 32ch cap in the side-by-side layout; full width when stacked (oneColumn).
              <div
                className={cn(
                  'max-w-full text-xs leading-4 text-muted-foreground',
                  sideBySide && '@tablet/form-field:max-w-[32ch]'
                )}
              >
                {description}
              </div>
            )}
          </div>
        )}
        <div
          className={cn(
            'isolate z-1',
            // Align chips with the label's baseline without affecting layout. Outline FieldGroups need no
            // nudge: their rows' 12px top padding matches the label's margin.
            !noStyle && sideBySide && '@tablet/form-field:[&>span>[data-slot=chip]]:translate-y-[7px]'
          )}
        >
          <span data-cy={`form-field-${id}`}>
            <Controller
              key={watchValue}
              name={id}
              control={control}
              defaultValue={defaultValue}
              rules={{
                ...rules,
                required: rules?.required === true ? `Please fill out the ${label} field` : rules?.required,
              }}
              render={({ field, fieldState }) => {
                if (!validationMode) {
                  return children(field, fieldState) as ReactElement;
                }
                const validatingField: ControllerRenderProps<T, Path<T>> = {
                  ...field,
                  onChange: (...event: unknown[]) => {
                    field.onChange(...event);
                    if (validationMode === 'onChange') {
                      void trigger(id);
                    }
                  },
                  onBlur: () => {
                    field.onBlur();
                    if (validationMode === 'onBlur') {
                      void trigger(id);
                    }
                  },
                };
                return children(validatingField, fieldState) as ReactElement;
              }}
            />
          </span>
        </div>
        {!noStyle && error && (
          <div
            className={cn(
              'col-span-full flex min-w-0 flex-col @tablet/form-field:mt-3',
              sideBySide && '@tablet/form-field:col-[2/-1] @wide/form-field:col-auto'
            )}
          >
            <p className="m-0 line-clamp-3 text-xs leading-4 [overflow-wrap:anywhere] text-destructive">
              {error.title}
            </p>
            {error.subtitle && (
              <span className="pt-0.5 text-xs text-muted-foreground [&_a]:font-normal">
                {error.onClick ? (
                  <UnderlineLink
                    href="#"
                    onClick={(event) => {
                      event.preventDefault();
                      error.onClick?.();
                    }}
                  >
                    {error.subtitle}
                  </UnderlineLink>
                ) : (
                  error.subtitle
                )}
              </span>
            )}
          </div>
        )}
      </div>
    </div>
  );
};
